import { FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import cx from "classnames";

import ProfileSwitcherStore from "./store";
import { UserSessionItemDto } from "./types";
import { getActiveSessionKey } from "../../utils/SsoSession";
import Button from "../Button";
import LoadingOverlay from "../Loading/overlay";
import "./style.css";

const AVATAR_COLORS = [
  "#1a73e8",
  "#d0021b",
  "#0b8043",
  "#8430ce",
  "#e37400",
  "#00838f",
  "#c5221f",
  "#3949ab",
];

const initialsOf = (name?: string, fallback?: string) => {
  const source = (name || fallback || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 2);
  return (parts[0][0] || "") + (parts[parts.length - 1][0] || "");
};

const colorOf = (seed?: string) => {
  const source = seed || "";
  let hash = 0;
  for (let i = 0; i < source.length; i++) {
    hash = (hash << 5) - hash + source.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface IAvatarProps {
  size: "sm" | "md" | "lg";
  name?: string;
  seed?: string;
  src?: string;
  ring?: boolean;
}

const Avatar: FC<IAvatarProps> = ({ size, name, seed, src, ring }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const showImage = !!src && !failed;

  return (
    <span
      className={cx("sso-ps__avatar", `sso-ps__avatar--${size}`, {
        "sso-ps__avatar--ring": ring,
      })}
      style={showImage ? undefined : { backgroundColor: colorOf(seed || name) }}
      aria-hidden="true"
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          width="100%"
          height="100%"
          style={{ objectFit: "cover" }}
          onError={() => setFailed(true)}
        />
      ) : (
        initialsOf(name, seed)
      )}
    </span>
  );
};

interface IProfileSwitcherProps {
  ProfileSwitcherStore?: ProfileSwitcherStore;
}

const ProfileSwitcher: FC<IProfileSwitcherProps> = inject("ProfileSwitcherStore")(
  observer((props: IProfileSwitcherProps) => {
    const { ProfileSwitcherStore: store } = props;
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(
      null
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const activeSessionKey = getActiveSessionKey();

    useEffect(() => {
      if (!store) return;
      if (!store.isLoaded || store.loadedFor !== activeSessionKey) store.load();
    }, [store, activeSessionKey]);

    const close = useCallback(() => {
      setIsOpen(false);
      triggerRef.current?.focus();
    }, []);

    const reposition = useCallback(() => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setAnchor({
        top: rect.bottom + 10,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    }, []);

    useEffect(() => {
      if (!isOpen) return;

      const onPointerDown = (event: MouseEvent | TouchEvent) => {
        const target = event.target as Node;
        if (
          !containerRef.current?.contains(target) &&
          !panelRef.current?.contains(target)
        ) {
          setIsOpen(false);
        }
      };
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") close();
      };

      document.addEventListener("mousedown", onPointerDown);
      document.addEventListener("touchstart", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
      window.addEventListener("resize", reposition);
      window.addEventListener("scroll", reposition, true);
      return () => {
        document.removeEventListener("mousedown", onPointerDown);
        document.removeEventListener("touchstart", onPointerDown);
        document.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("resize", reposition);
        window.removeEventListener("scroll", reposition, true);
      };
    }, [isOpen, close, reposition]);

    if (!store) return null;

    const busyOverlay = <LoadingOverlay show={store.isBusy} />;

    if (!store.isLoaded || !store.isSwitchAvailable) return busyOverlay;

    const current = store.current;
    if (!current) return busyOverlay;

    const toggle = () => {
      const next = !isOpen;
      if (next) reposition();
      setIsOpen(next);
    };

    const onSelect = (session: UserSessionItemDto) => {
      if (session.isCurrent) return;
      store.switchTo(session);
    };

    const endConfirmText = (session: UserSessionItemDto) =>
      `${session.name || session.userName} ${t(
        "session will be ended. Do you confirm?"
      )}`;

    return (
      <div className="sso-ps" ref={containerRef}>
        {busyOverlay}
        <button
          type="button"
          ref={triggerRef}
          className="sso-ps__trigger"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label={t("Switch Profile")}
          title={current.name || current.userName}
          disabled={store.isBusy}
        >
          <Avatar
            size="md"
            name={current.name}
            seed={current.sessionKey}
            src={current.avatar}
            ring={!current.isRoot}
          />
        </button>

        {isOpen &&
          createPortal(
            <div
              className="sso-ps__panel"
              role="menu"
              ref={panelRef}
              style={
                anchor ? { top: anchor.top, right: anchor.right } : undefined
              }
            >
            <div className="sso-ps__head">
              <Avatar
                size="lg"
                name={current.name}
                seed={current.sessionKey}
                src={current.avatar}
                ring={!current.isRoot}
              />
              <div className="sso-ps__head-name text-truncate">
                {current.name || current.userName}
              </div>
              <div className="sso-ps__head-sub text-truncate">
                {current.email || current.userName}
              </div>
              <span
                className={cx("sso-ps__badge", {
                  "sso-ps__badge--root": current.isRoot,
                  "sso-ps__badge--proxy": !current.isRoot,
                })}
              >
                {current.isRoot ? t("Main Profile") : t("Proxy Login")}
              </span>
            </div>

            <div className="sso-ps__list">
              {store.others.length === 0 ? (
                <div className="sso-ps__empty">{t("No other open sessions")}</div>
              ) : (
                store.others.map((session) => (
                  <div
                    key={session.sessionKey}
                    className="sso-ps__item"
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => onSelect(session)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelect(session);
                      }
                    }}
                  >
                    <Avatar
                      size="sm"
                      name={session.name}
                      seed={session.sessionKey}
                      src={session.avatar}
                    />
                    <span className="sso-ps__item-body">
                      <span className="sso-ps__item-name d-block text-truncate">
                        {session.name || session.userName}
                      </span>
                      <span className="sso-ps__item-sub d-block text-truncate">
                        {session.userName || session.email}
                      </span>
                      <span
                        className={cx(
                          "sso-ps__badge",
                          "sso-ps__badge--sm",
                          {
                            "sso-ps__badge--root": session.isRoot,
                            "sso-ps__badge--proxy": !session.isRoot,
                          }
                        )}
                      >
                        {session.isRoot ? t("Main Profile") : t("Proxy Login")}
                      </span>
                    </span>
                    {!session.isRoot && (
                      <span
                        className="sso-ps__item-end-wrap"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Button
                          className="sso-ps__item-end"
                          icon={<span aria-hidden="true">&times;</span>}
                          aria-label={t("End This Session")}
                          showConfirm
                          confirmText={endConfirmText(session)}
                          buttonClassName="danger"
                          buttonText={t("End This Session")}
                          onClick={() => store.end(session)}
                          disabled={store.isBusy}
                        />
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="sso-ps__foot">
              {store.sessions.some((s) => !s.isRoot) && (
                <Button
                  className="btn btn-outline-secondary"
                  text={t("End Proxy Logins")}
                  showConfirm
                  confirmText={t(
                    "All proxy logins will be ended and only your main profile will remain open. Do you confirm?"
                  )}
                  buttonClassName="danger"
                  buttonText={t("End Proxy Logins")}
                  onClick={() => store.endOthers()}
                  disabled={store.isBusy}
                />
              )}
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => store.logout()}
                disabled={store.isBusy}
              >
                {t("Safe Exit")}
              </button>
            </div>
            </div>,
            document.body
          )}
      </div>
    );
  })
);

export default ProfileSwitcher;
